import React, {useRef, useState, useCallback, useEffect} from 'react';
import {gsap} from 'gsap';
import {Search, X} from 'lucide-react';
import Box from '@mui/material/Box';
import ResultBox from '@/ui/search-box/ResultBox';
import {Blog, BlogCategoryDetails, CategoryDetails, SubCategory} from '@/types';
import servicesObject from '@/content/services';
import blogsObject from '@/content/blogs';

export type SearchResult = {
    name: string;
    slug: string;
    image: string;
    description: string;
    categoryType: string;
    categorySlug: string;
};

interface Props {
    debounceTime?: number;
}

const allSubCategories = Object.values(servicesObject).flatMap(
    (s: CategoryDetails) => s.subCategories
);

const allBlogs = Object.values(blogsObject).flatMap(
    (b: BlogCategoryDetails) => b.blogs
);

const searchPool = [...allSubCategories, ...allBlogs];


const SearchBox: React.FC<Props> = ({debounceTime = 300}) => {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState<SearchResult[]>([]);
    const [isBackdropVisible, setBackdropVisible] = useState(false);
    const backdropRef = useRef<HTMLDivElement | null>(null);
    const resultsContainerRef = useRef<HTMLDivElement | null>(null);

    let debounceTimer: NodeJS.Timeout;

    const handleSearch = useCallback((searchTerm: string) => {
        const lowerCaseTerm = searchTerm.toLowerCase();
        const filteredResults = searchPool.filter((item) => {
            const searchableText = [
                item.title || '',
                item.description || '',
                item.slug || '',
                item.content?.map((block) => block.content).join(' ') || '' // Flatten content blocks
            ]
                .join(' ')
                .toLowerCase();
            return searchableText.includes(lowerCaseTerm);
        });

        setResults(
            filteredResults.map((item) => {
                if ('headCategory' in item) {
                    // Treat as SubCategory
                    const subCategory = item as SubCategory;
                    return {
                        name: subCategory.title || '',
                        slug: subCategory.slug || '',
                        image: subCategory.images?.[0]?.original || '',
                        description: subCategory.description || '',
                        categoryType: subCategory.headCategory?.categoryType || '',
                        categorySlug: subCategory.headCategory?.slug || '',
                    };
                } else {
                    // Treat as Blog
                    const blog = item as Blog;
                    return {
                        name: blog.title || '',
                        slug: blog.slug || '',
                        image: blog.heroImage?.url || '',
                        description: blog.description || '',
                        categoryType: blog.blogCategory?.categoryType || '',
                        categorySlug: blog.blogCategory?.slug || '',
                    };
                }
            })
        );
    }, []);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                setBackdropVisible(false);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    useEffect(() => {
        if (isBackdropVisible && backdropRef.current) {
            gsap.fromTo(
                backdropRef.current,
                {opacity: 0},
                {opacity: 1, duration: 0.5, ease: 'power2.out'}
            );
        } else if (backdropRef.current) {
            gsap.to(backdropRef.current, {
                opacity: 0,
                scale: 0.95,
                duration: 0.5,
                ease: 'power2.in',
                onComplete: () => {
                    setBackdropVisible(false);
                    setResults([]); // Clear the search results
                },
            });
        }
    }, [isBackdropVisible]);


    return (
        <>
            {/* Trigger to show the backdrop */}
            <div className="width-full p-3 bg-black">
                <Search
                    id="trigger"
                    className="w-5 h-5 cursor-pointer text-white"
                    onClick={() => setBackdropVisible(true)}
                />
            </div>

            {/* Backdrop */}
            {isBackdropVisible && (
                <Box
                    ref={backdropRef}
                    className="fixed inset-0 z-51"
                    id="backdrop"
                    onClick={(e) => {
                        if ((e.target as HTMLElement).id === 'backdrop') {
                            setBackdropVisible(false);
                        }
                    }}
                    sx={(theme) => ({
                        ...theme.custom.headerMobileNav,
                    })}
                >
                    {/* Close button */}
                    <X
                        className="absolute top-4 right-4 cursor-pointer w-6 h-6 text-gray-500"
                        onClick={() => setBackdropVisible(false)}
                    />

                    <Box
                        className="p-4 rounded relative max-w-[670px] w-[670px] mx-auto top-[5%] h-[90%] flex flex-col"
                        sx={(theme) => ({
                            backgroundColor: theme.palette.background.paper,
                            [theme.breakpoints.down('sm')]: {
                                width: '90%',
                                height: '80%',
                            }
                        })}
                    >
                        <div className="relative pb-3">
                            <Search
                                className="absolute absolute top-[12px] left-[9px] w-5 h-5"/>
                            <input
                                type="text"
                                placeholder="Search..."
                                value={query}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setQuery(value);

                                    if (value.length < 3) {
                                        setResults([]); // Clear results if query has less than 3 letters
                                        return;
                                    }

                                    clearTimeout(debounceTimer);
                                    debounceTimer = setTimeout(() => {
                                        handleSearch(value);
                                    }, debounceTime);
                                }}
                                className="w-full bg-white border px-10 py-2 rounded shadow focus:outline-none"
                            />
                            {/*{!query && (*/}
                            {/*    <Search*/}
                            {/*        className="absolute top-[22px] left-[8px] w-5 h-5"/>*/}
                            {/*)}*/}
                        </div>
                        <div className="mt-0 mb-2 mx-2 overflow-y-auto">
                            {query.length >= 3 && results.length === 0 ? (
                                <p className="text-gray-500">No results...</p>
                            ) : query.length <= 3 && results.length === 0 ?
                                <p className="text-gray-500">Type to search...</p> :
                                <div ref={resultsContainerRef}>
                                    {results.map((searchResult) => (
                                        <ResultBox key={searchResult.slug} {...searchResult} />
                                    ))}
                                </div>
                            }
                        </div>
                    </Box>
                </Box>
            )}
        </>
    );
};

export default SearchBox;
